import Debug from '../utils/Debug.js';
import SeededRandom from '../utils/SeededRandom.js';
import { SPELL_PROJECTILES } from '../combat/CombatStats.js';

/**
 * NPCBehavior - AI state machine for NPC characters.
 * Supports wander (random nearby movement) and patrol (waypoint cycle) behaviors.
 * Hostile NPCs have combat AI with pursuit persistence and group aggro.
 */
export default class NPCBehavior {
  // Idle time range (seconds)
  static IDLE_TIME_MIN = 2;
  static IDLE_TIME_MAX = 6;

  // Dungeon NPCs are more active
  static DUNGEON_IDLE_TIME_MIN = 1.0;
  static DUNGEON_IDLE_TIME_MAX = 3.0;

  // Wander defaults
  static DEFAULT_WANDER_RADIUS = 5;
  static WANDER_MIN_DISTANCE = 2;

  // Patrol idle at each waypoint (seconds)
  static PATROL_WAIT_MIN = 1;
  static PATROL_WAIT_MAX = 3;

  // Max consecutive path failures before longer cooldown
  static MAX_FAILURES = 3;
  static FAILURE_COOLDOWN = 4; // seconds

  // Max attempts to find a safe wander path before giving up
  static WANDER_MAX_ATTEMPTS = 5;

  // Edge margin when picking points on surfaces (stay away from edges)
  static SURFACE_EDGE_MARGIN = 0.5; // Unified with PathFollower.EDGE_CLAMP_DISTANCE

  // Max Y difference for candidate wander surfaces (generous — return-path check ensures safety)
  static WANDER_Y_TOLERANCE = 10;

  // Chase/pursuit constants
  static PURSUIT_DURATION = 5.0;       // seconds to keep chasing after player leaves aggro range
  static PURSUIT_MAX_RANGE = 20;       // hard deaggro range regardless of pursuit timer
  static CHASE_REPATH_INTERVAL = 0.5;  // normal repath interval (seconds)
  static CHASE_REPATH_CLOSE = 0.25;    // faster repath when close to target

  // Group aggro constants
  static ALERT_RANGE = 10;        // world units radius for alerting nearby allies
  static ALERT_COOLDOWN = 3.0;    // seconds between alert broadcasts

  // Threat tracking constants (damage attribution)
  static THREAT_DURATION = 3.0;     // seconds to prioritize attacker after being hit
  static THREAT_BIAS_RANGE = 12;    // max range to consider threat position valid

  /**
   * @param {Character} character - The NPC character
   * @param {Object} options
   * @param {'wander'|'patrol'} options.type - Behavior type
   * @param {PathFinder} options.pathFinder - Pathfinding system
   * @param {{x,y,z}} options.spawnPoint - NPC's home position (wander origin)
   * @param {number} [options.wanderRadius=5] - Wander radius from spawn
   * @param {{x:number,y:number,z:number}[]} [options.patrolPoints=[]] - Patrol waypoints
   * @param {number} [options.seed] - Seed for deterministic PRNG (multiplayer sync)
   */
  constructor(character, options = {}) {
    this.character = character;
    this.type = options.type || 'wander';
    this.pathFinder = options.pathFinder;
    this.spawnPoint = options.spawnPoint || { x: character.x, y: character.y, z: character.z };
    this.wanderRadius = options.wanderRadius || NPCBehavior.DEFAULT_WANDER_RADIUS;
    this.patrolPoints = options.patrolPoints || [];
    this.currentPatrolIndex = 0;

    // Seeded PRNG for deterministic behavior across multiplayer peers
    const seed = options.seed || SeededRandom.hashString(character.id);
    this.rng = new SeededRandom(seed);

    // State machine
    this.state = 'idle'; // 'idle' | 'walking' | 'chase' | 'attack'
    this.idleTimer = 0;
    this.nextIdleTime = this._randomIdleTime();

    // Failure tracking
    this._consecutiveFailures = 0;

    // Combat chase state
    this._chaseRepathTimer = 0;
    this._pursuitTimer = 0;
    this._attackCooldownTimer = 0;

    // Group aggro
    this._alertCooldown = 0;
    this._npcManager = null; // set by NPCManager after construction

    // Damage threat tracking: position of last attacker + decay timer
    this._threatPosition = null;  // { x, z }
    this._threatTimer = 0;

    // Dungeon mode flag (shorter idle times)
    this.dungeonMode = false;
  }

  /**
   * Update behavior each frame
   * @param {number} deltaTime - Seconds since last frame
   */
  update(deltaTime) {
    // Frozen by dialog system
    if (this._dialogPaused) return;

    // Dead NPCs do nothing
    if (this.character.combatStats && this.character.combatStats.isDead) return;

    // NPC mana regeneration (spell-casting NPCs recover mana over time)
    const stats = this.character.combatStats;
    if (stats && stats.maxMana > 0 && stats.mana < stats.maxMana) {
      stats.mana = Math.min(stats.maxMana, stats.mana + stats.manaRegen * deltaTime);
    }

    // Combat AI: hostile NPCs check for player and fight
    if (this.combatConfig && this.combatConfig.isHostile) {
      const handled = this._updateCombatAI(deltaTime);
      if (handled) return; // combat AI consumed this frame
    }

    switch (this.state) {
      case 'idle':
        this.idleTimer += deltaTime;
        if (this.idleTimer >= this.nextIdleTime) {
          this._startNextMove();
        }
        break;

      case 'walking':
        // PathFollower handles actual movement.
        // Check if path following finished (arrived or stopped).
        if (!this.character.pathFollower || !this.character.pathFollower.isFollowing) {
          this._enterIdle();
        }
        break;
    }
  }

  // ---- Combat AI ----

  /**
   * Update hostile NPC combat behavior.
   * Returns true if combat AI handled this frame (skip normal behavior).
   * @private
   */
  _updateCombatAI(deltaTime) {
    // Decrement alert cooldown
    if (this._alertCooldown > 0) this._alertCooldown -= deltaTime;

    const player = this._findTargetPlayer(deltaTime);
    if (!player) {
      // No valid target — return to normal behavior
      if (this.state === 'chase' || this.state === 'attack') {
        this._enterIdle();
      }
      return false;
    }

    const dist = this._distanceTo(player);
    // Effective attack range: use ranged range if NPC has a projectile weapon
    const effectiveRange = this.combatConfig.rangedAttackRange || this.combatConfig.attackRange;

    switch (this.state) {
      case 'chase':
        // Re-sight: reset pursuit timer when player is within aggro range
        if (dist <= this.combatConfig.aggroRange) {
          this._pursuitTimer = NPCBehavior.PURSUIT_DURATION;
        }
        // Hard deaggro at max pursuit range
        if (dist > NPCBehavior.PURSUIT_MAX_RANGE) {
          this._enterIdle();
          return false;
        }
        // Pursuit persistence: keep chasing for PURSUIT_DURATION after losing sight
        if (dist > this.combatConfig.deaggroRange) {
          this._pursuitTimer -= deltaTime;
          if (this._pursuitTimer <= 0) {
            this._enterIdle();
            return false;
          }
        }
        if (dist <= effectiveRange) {
          this._enterAttackState(player);
          return true;
        }
        this._chasePlayer(player, deltaTime);
        return true;

      case 'attack':
        this._attackCooldownTimer -= deltaTime;
        if (this._attackCooldownTimer <= 0) {
          // Cooldown done, re-evaluate
          if (dist > effectiveRange) {
            this.state = 'chase';
            this._chasePlayer(player, deltaTime);
          } else {
            this._enterAttackState(player);
          }
        }
        return true;

      default:
        // idle or walking — check aggro
        if (dist <= this.combatConfig.aggroRange) {
          this.state = 'chase';
          this._chaseRepathTimer = 0;
          this._pursuitTimer = NPCBehavior.PURSUIT_DURATION;
          this._chasePlayer(player, deltaTime);
          // Alert nearby allies to chase the same target
          this._alertNearbyAllies(player);
          return true;
        }
        return false;
    }
  }

  /**
   * Record that this NPC was attacked from a position.
   * Causes the NPC to prioritize targets near that position briefly.
   * @param {number} ax - Attacker X position
   * @param {number} az - Attacker Z position
   */
  setThreatPosition(ax, az) {
    this._threatPosition = { x: ax, z: az };
    this._threatTimer = NPCBehavior.THREAT_DURATION;
  }

  /**
   * Find the best player target, factoring in threat bias from recent damage.
   * Prioritizes the player nearest to the last attacker's position if threat is fresh.
   * Falls back to nearest player otherwise.
   * @param {number} deltaTime
   * @returns {Character|null}
   * @private
   */
  _findTargetPlayer(deltaTime) {
    // Decay threat timer
    if (this._threatTimer > 0) {
      this._threatTimer -= deltaTime;
      if (this._threatTimer <= 0) {
        this._threatPosition = null;
      }
    }

    // If there's an active threat position, prefer a player close to it
    if (this._threatPosition) {
      const threatTarget = this._findPlayerNearPosition(
        this._threatPosition.x, this._threatPosition.z
      );
      if (threatTarget) return threatTarget;
    }

    // Default: nearest player
    return this._findNearestPlayer();
  }

  /**
   * Find the nearest living player character (local or remote).
   * @returns {Character|null}
   * @private
   */
  _findNearestPlayer() {
    const world = this.character.world;
    if (!world || !world.characters) return null;

    let nearest = null;
    let nearestDistSq = Infinity;
    const cx = this.character.x;
    const cy = this.character.y;
    const cz = this.character.z;

    for (const char of world.characters.values()) {
      if (char.isNPC) continue;
      if (char.combatStats && char.combatStats.isDead) continue;

      const dx = char.x - cx;
      const dy = char.y - cy;
      const dz = char.z - cz;
      const distSq = dx * dx + dy * dy + dz * dz;

      if (distSq < nearestDistSq) {
        nearestDistSq = distSq;
        nearest = char;
      }
    }
    return nearest;
  }

  /**
   * Find the nearest living player to a specific world position.
   * Used for threat-based targeting (chase toward where damage came from).
   * @param {number} px - Position X
   * @param {number} pz - Position Z
   * @returns {Character|null}
   * @private
   */
  _findPlayerNearPosition(px, pz) {
    const world = this.character.world;
    if (!world || !world.characters) return null;

    let nearest = null;
    let nearestDistSq = NPCBehavior.THREAT_BIAS_RANGE * NPCBehavior.THREAT_BIAS_RANGE;

    for (const char of world.characters.values()) {
      if (char.isNPC) continue;
      if (char.combatStats && char.combatStats.isDead) continue;

      const dx = char.x - px;
      const dz = char.z - pz;
      const distSq = dx * dx + dz * dz;

      if (distSq < nearestDistSq) {
        nearestDistSq = distSq;
        nearest = char;
      }
    }
    return nearest;
  }

  /** @private */
  _distanceTo(target) {
    const dx = target.x - this.character.x;
    const dy = target.y - this.character.y;
    const dz = target.z - this.character.z;
    return Math.sqrt(dx * dx + dy * dy + dz * dz);
  }

  /** @private */
  _chasePlayer(player, deltaTime) {
    this._chaseRepathTimer = (this._chaseRepathTimer || 0) + deltaTime;

    // Repath interval: faster when close to target for tighter tracking
    const dist = this._distanceTo(player);
    const effectiveRange = this.combatConfig.rangedAttackRange || this.combatConfig.attackRange || 1.8;
    const repathInterval = dist < effectiveRange * 2
      ? NPCBehavior.CHASE_REPATH_CLOSE
      : NPCBehavior.CHASE_REPATH_INTERVAL;

    if (this._chaseRepathTimer >= repathInterval) {
      this._chaseRepathTimer = 0;
      const from = { x: this.character.x, y: this.character.y, z: this.character.z };
      const to = { x: player.x, y: player.y, z: player.z };
      const path = this.pathFinder.findPath(from, to);
      if (path && path.length > 0) {
        this.character.pathFollower.setPath(path);
      }
    }
    // PathFollower handles facing via movement direction — no manual override needed
  }

  /** @private */
  _enterAttackState(player) {
    this.state = 'attack';

    // Stop movement
    if (this.character.pathFollower) {
      this.character.pathFollower.stop();
    }

    // Face the player
    const dx = player.x - this.character.x;
    const dz = player.z - this.character.z;
    const angle = Math.atan2(dz, dx) * 180 / Math.PI;
    this.character.setFacing(angle);

    const projectileType = this.combatConfig.projectileType;
    const isSpell = projectileType && SPELL_PROJECTILES.includes(projectileType);
    const isRanged = this.combatConfig.rangedAttackRange > 0
      && projectileType
      && this.projectileManager;
    const dist = this._distanceTo(player);

    // Mana check for spell-casting NPCs: if out of mana, fall back to melee
    const stats = this.character.combatStats;
    const manaCost = isSpell ? 20 : 0;
    const canCastSpell = isSpell && stats && stats.mana >= manaCost;

    if (isRanged && dist > this.combatConfig.attackRange && (!isSpell || canCastSpell)) {
      // Ranged/spell attack: fire projectile
      if (isSpell && stats) stats.mana -= manaCost;

      const config = this.character.config || {};
      const attackAnim = config.special || config.punch || 'weapon-shoot';
      this.character.triggerAction(attackAnim);

      // Spawn projectile after a short delay (matching animation wind-up)
      setTimeout(() => {
        if (this.character.combatStats?.isDead) return;
        if (projectileType === 'fireball') {
          this.projectileManager.spawnFireball(this.character);
        } else {
          this.projectileManager.spawnProjectile(this.character, projectileType);
        }
      }, 200);
    } else {
      // Melee attack
      const config = this.character.config || {};
      const attackAnim = config.punch || 'lead-jab';
      this.character.triggerAction(attackAnim);

      // Register with combat system for melee hit detection
      if (this.combatManager) {
        this.combatManager.registerAttack(this.character, 'light');
      }
    }

    // Play attack vocal
    if (this._audioSystem) {
      this._audioSystem.playEnemyAttackSound(this.character);
    }

    // Set cooldown
    this._attackCooldownTimer = this.combatConfig.attackCooldown || 1.0;
  }

  // ---- Group Aggro ----

  /**
   * Alert nearby hostile NPCs to join the fight.
   * Called when this NPC first enters chase state.
   * @param {Character} target - The player that triggered the aggro
   * @private
   */
  _alertNearbyAllies(target) {
    if (!this._npcManager) return;
    if (this._alertCooldown > 0) return;
    this._alertCooldown = NPCBehavior.ALERT_COOLDOWN;

    if (!target) return;

    const allies = this._npcManager.getHostileNPCsInRange(
      this.character, NPCBehavior.ALERT_RANGE
    );
    for (const { behavior } of allies) {
      behavior.alertToPlayer(target);
    }
  }

  /**
   * Externally alert this NPC to chase the player.
   * Called by a nearby NPC that has already aggro'd.
   * @param {Character} player - The player to chase
   */
  alertToPlayer(player) {
    // Already fighting or dead — ignore
    if (this.state === 'chase' || this.state === 'attack') return;
    if (this.character.combatStats && this.character.combatStats.isDead) return;

    this.state = 'chase';
    this._chaseRepathTimer = 0;
    this._pursuitTimer = NPCBehavior.PURSUIT_DURATION;
    Debug.log('NPC', `${this.character.id} alerted to player by ally`);
  }

  // ---- Wander / Patrol ----

  /**
   * Start the next move based on behavior type
   * @private
   */
  _startNextMove() {
    if (this.type === 'patrol' && this.patrolPoints.length > 0) {
      this._moveToNextPatrolPoint();
    } else {
      this._moveToRandomPoint();
    }
  }

  /**
   * Pick a random walkable position near spawnPoint and path to it.
   * Picks destinations from actual walkable surfaces (like click-to-move validates
   * destinations against real cuboid surfaces) so NPCs never target off-surface points.
   * Rejects paths where the NPC can't return (one-way drops).
   * @private
   */
  _moveToRandomPoint() {
    const from = { x: this.character.x, y: this.character.y, z: this.character.z };

    // Get candidate walkable surfaces within wander radius
    const candidates = this._getWanderCandidates();
    if (candidates.length === 0) {
      Debug.log('NPC', `${this.character.id} no walkable surfaces within wander radius`);
      this._handlePathFailure();
      return;
    }

    for (let attempt = 0; attempt < NPCBehavior.WANDER_MAX_ATTEMPTS; attempt++) {
      // Pick a random candidate surface
      const node = candidates[this.rng.nextInt(0, candidates.length)];

      // Pick a random safe point on that surface (with edge margin)
      const destination = this._pickSafePointOnNode(node);

      // Relax minimum distance on later attempts so NPCs on small platforms still move
      const effectiveMinDist = attempt < 3 ? NPCBehavior.WANDER_MIN_DISTANCE : 0.5;
      const dx = destination.x - from.x;
      const dz = destination.z - from.z;
      if (dx * dx + dz * dz < effectiveMinDist * effectiveMinDist) {
        continue;
      }

      const path = this.pathFinder.findPath(from, destination);

      if (path && path.length > 0 && this._isPathSafe(path, from)) {
        this.character.pathFollower.setPath(path);
        this.state = 'walking';
        this._consecutiveFailures = 0;
        Debug.log('NPC', `${this.character.id} wandering to (${destination.x.toFixed(1)}, ${destination.z.toFixed(1)}) on surface y=${node.y}`);
        return;
      }
    }

    // All attempts failed — try returning to spawn as recovery
    if (this._tryReturnToSpawn(from)) return;

    this._handlePathFailure();
  }

  /**
   * Get candidate walkable surfaces within wander radius of spawn point.
   * Uses closest-point-on-surface distance so large surfaces partially inside
   * the radius are included.
   * @returns {NavNode[]}
   * @private
   */
  _getWanderCandidates() {
    const navGraph = this.pathFinder.navGraph;
    if (!navGraph) return [];

    const candidates = [];
    const spawnX = this.spawnPoint.x;
    const spawnZ = this.spawnPoint.z;
    const charY = this.character.y;
    const radiusSq = this.wanderRadius * this.wanderRadius;

    for (const node of navGraph.getAllNodes()) {
      // Use closest point on node surface for distance check (handles large surfaces)
      const closestX = Math.max(node.minX, Math.min(node.maxX, spawnX));
      const closestZ = Math.max(node.minZ, Math.min(node.maxZ, spawnZ));
      const dx = closestX - spawnX;
      const dz = closestZ - spawnZ;
      if (dx * dx + dz * dz > radiusSq) continue;

      // Height filter — generous range; _isPathSafe enforces step-by-step safety
      if (Math.abs(node.y - charY) > NPCBehavior.WANDER_Y_TOLERANCE) continue;

      candidates.push(node);
    }

    return candidates;
  }

  /**
   * Pick a random point safely inside a nav node's surface bounds.
   * Applies edge margin to keep NPC away from surface edges.
   * @param {NavNode} node
   * @returns {{x: number, y: number, z: number}}
   * @private
   */
  _pickSafePointOnNode(node) {
    const MARGIN = NPCBehavior.SURFACE_EDGE_MARGIN;

    // Calculate safe bounds with edge margin
    let minX = node.minX + MARGIN;
    let maxX = node.maxX - MARGIN;
    let minZ = node.minZ + MARGIN;
    let maxZ = node.maxZ - MARGIN;

    // Handle tiny surfaces where margin exceeds half the dimension — use center
    if (minX >= maxX) {
      minX = maxX = (node.minX + node.maxX) / 2;
    }
    if (minZ >= maxZ) {
      minZ = maxZ = (node.minZ + node.maxZ) / 2;
    }

    const x = minX + this.rng.next() * (maxX - minX);
    const z = minZ + this.rng.next() * (maxZ - minZ);

    return { x, y: node.y, z };
  }

  /**
   * Try to path back to spawn point as a recovery when stuck.
   * @param {{x,y,z}} from - Current position
   * @returns {boolean} Whether a safe path to spawn was found
   * @private
   */
  _tryReturnToSpawn(from) {
    const dx = from.x - this.spawnPoint.x;
    const dz = from.z - this.spawnPoint.z;
    // Only attempt if we've actually drifted away from spawn
    if (dx * dx + dz * dz < NPCBehavior.WANDER_MIN_DISTANCE * NPCBehavior.WANDER_MIN_DISTANCE) {
      return false;
    }

    const path = this.pathFinder.findPath(from, this.spawnPoint);
    if (path && path.length > 0) {
      this.character.pathFollower.setPath(path);
      this.state = 'walking';
      this._consecutiveFailures = 0;
      Debug.log('NPC', `${this.character.id} returning to spawn`);
      return true;
    }
    return false;
  }

  /**
   * Check whether a path is safe for NPC traversal.
   * Instead of rejecting based on Y-drop heuristics, verifies that a return path
   * exists — if the NPC can get back, the route is safe regardless of height changes.
   * @param {Array<{x,y,z,type?}>} path
   * @param {{x,y,z}} from - Starting position (needed for return-path check)
   * @returns {boolean}
   * @private
   */
  _isPathSafe(path, from) {
    if (!from || path.length === 0) return true;

    // The destination is the last waypoint in the path
    const dest = path[path.length - 1];

    // Check if there's a return path from destination back to origin
    const returnPath = this.pathFinder.findPath(dest, from);
    return returnPath != null && returnPath.length > 0;
  }

  /**
   * Move to the next patrol waypoint
   * @private
   */
  _moveToNextPatrolPoint() {
    const target = this.patrolPoints[this.currentPatrolIndex];
    const from = { x: this.character.x, y: this.character.y, z: this.character.z };
    const path = this.pathFinder.findPath(from, target);

    if (path && path.length > 0 && this._isPathSafe(path, from)) {
      this.character.pathFollower.setPath(path);
      this.state = 'walking';
      this.currentPatrolIndex = (this.currentPatrolIndex + 1) % this.patrolPoints.length;
      this._consecutiveFailures = 0;
      Debug.log('NPC', `${this.character.id} patrolling to point ${this.currentPatrolIndex}`);
    } else {
      // Skip to next patrol point on failure
      this.currentPatrolIndex = (this.currentPatrolIndex + 1) % this.patrolPoints.length;
      this._handlePathFailure();
    }
  }

  /**
   * Handle path finding failure
   * @private
   */
  _handlePathFailure() {
    this._consecutiveFailures++;
    if (this._consecutiveFailures >= NPCBehavior.MAX_FAILURES) {
      // Back off with longer cooldown
      this._enterIdle(NPCBehavior.FAILURE_COOLDOWN);
      Debug.warn('NPC', `${this.character.id} failed ${this._consecutiveFailures} times, cooling down`);
    } else {
      // Short retry delay
      this._enterIdle(1);
    }
  }

  /**
   * Enter idle state
   * @param {number} [forceDuration] - Override idle duration (seconds)
   * @private
   */
  _enterIdle(forceDuration) {
    this.state = 'idle';
    this.idleTimer = 0;
    this.nextIdleTime = forceDuration != null ? forceDuration : this._randomIdleTime();
  }

  /**
   * Generate random idle duration
   * @private
   */
  _randomIdleTime() {
    let min, max;
    if (this.type === 'patrol') {
      min = NPCBehavior.PATROL_WAIT_MIN;
      max = NPCBehavior.PATROL_WAIT_MAX;
    } else if (this.dungeonMode) {
      min = NPCBehavior.DUNGEON_IDLE_TIME_MIN;
      max = NPCBehavior.DUNGEON_IDLE_TIME_MAX;
    } else {
      min = NPCBehavior.IDLE_TIME_MIN;
      max = NPCBehavior.IDLE_TIME_MAX;
    }
    return min + this.rng.next() * (max - min);
  }

  /**
   * Capture full runtime state for multiplayer sync.
   * @returns {Object}
   */
  getState() {
    return {
      state: this.state,
      idleTimer: this.idleTimer,
      nextIdleTime: this.nextIdleTime,
      _consecutiveFailures: this._consecutiveFailures,
      currentPatrolIndex: this.currentPatrolIndex,
      _chaseRepathTimer: this._chaseRepathTimer,
      _pursuitTimer: this._pursuitTimer,
      _attackCooldownTimer: this._attackCooldownTimer,
      _alertCooldown: this._alertCooldown,
      rngState: this.rng.getState()
    };
  }

  /**
   * Restore runtime state from serialized data (multiplayer sync / save).
   * @param {Object} saved
   */
  setState(saved) {
    if (!saved) return;
    this.state = saved.state || 'idle';
    this.idleTimer = saved.idleTimer || 0;
    this.nextIdleTime = saved.nextIdleTime != null ? saved.nextIdleTime : this._randomIdleTime();
    this._consecutiveFailures = saved._consecutiveFailures || 0;
    this.currentPatrolIndex = saved.currentPatrolIndex || 0;
    this._chaseRepathTimer = saved._chaseRepathTimer || 0;
    this._pursuitTimer = saved._pursuitTimer || 0;
    this._attackCooldownTimer = saved._attackCooldownTimer || 0;
    this._alertCooldown = saved._alertCooldown || 0;
    if (saved.rngState != null) {
      this.rng.setState(saved.rngState);
    }
    // Can't restore walking/chase/attack states — path and combat target aren't serialized.
    // Force idle with a short fixed duration (no RNG consumed) to keep sync with host.
    // The host's RNG state was captured mid-walk; when this peer's NPC next picks a
    // wander target, it will consume the same RNG values the host consumes upon arrival.
    if (this.state === 'walking' || this.state === 'chase' || this.state === 'attack') {
      this.state = 'idle';
      this.idleTimer = 0;
      this.nextIdleTime = 0.5; // Fixed duration, no RNG consumed
      // Stop any active path — otherwise pathFollower continues moving the NPC
      // while behavior is idle, potentially walking off edges
      if (this.character.pathFollower) {
        this.character.pathFollower.stop();
      }
    }
  }

  /**
   * Serialize behavior config
   */
  toJSON() {
    return {
      type: this.type,
      spawnPoint: { ...this.spawnPoint },
      wanderRadius: this.wanderRadius,
      patrolPoints: this.patrolPoints.map(p => ({ x: p.x, y: p.y, z: p.z }))
    };
  }
}
